import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { SharedModule } from "../../shared.module";
import { HeaderDialogComponent } from "../../modules/dialog/header-dialog/header-dialog.component";
import { ChipsToggleComponent } from "../../modules/chips-toggle/chips-toggle.component";
import { BigButtonComponent } from "../big-button/big-button.component";
import { DatetimePickerComponent } from "../../modules/datetime-picker/datetime-picker.component";

/**
 * Toggle générique passé au dialog. L'`id` sert à identifier le paramètre
 * dans le résultat retourné. Le `value` est l'état initial.
 */
export interface ExcelExportToggle {
  id: string;
  label: string;
  value: boolean;
  hint?: string;
}

/**
 * Données d'entrée du dialog de confirmation d'export Excel. Le caller
 * fournit les options actuelles (période + toggles), le dialog retourne
 * les options validées par l'utilisateur (potentiellement modifiées).
 */
export interface ExcelExportConfirmData {
  /** Titre du dialog (ex: "Exporter — Stats transport"). */
  title: string;
  /** Sous-titre optionnel pour rappeler le contexte (ex: nom du tiers). */
  subtitle?: string;
  /** Options de période proposées (ex: 3M / 6M / 12M / 24M). */
  periodOptions?: { name: string; value: any }[];
  /** Période actuellement sélectionnée. */
  currentPeriod?: any;
  /** Si true, l'utilisateur peut basculer en plage personnalisée
   *  (datepickers début/fin) au lieu d'un preset. */
  allowCustomRange?: boolean;
  /** Plage personnalisée initiale (ISO `yyyy-MM-dd`). */
  initialCustomStart?: string | null;
  initialCustomEnd?: string | null;
  /** Toggles génériques (ex: "Inclure annulées", "Inclure MO"). */
  toggles?: ExcelExportToggle[];
  /** Texte du bouton de confirmation (défaut "Générer le fichier"). */
  confirmLabel?: string;
}

/**
 * Résultat retourné par le dialog. `null` si l'utilisateur annule.
 * Sinon, contient les paramètres validés. Le caller compare avec ses
 * paramètres actuels pour décider s'il doit recharger les données ou non.
 */
export interface ExcelExportConfirmResult {
  /** Période sélectionnée (présent uniquement si pas en custom range). */
  period?: any;
  /** Plage personnalisée (présente si l'utilisateur a basculé). */
  customStart?: string;
  customEnd?: string;
  /** Map des toggles par id. */
  toggles?: Record<string, boolean>;
}

/**
 * Dialog de confirmation d'export Excel — pré-remplit avec les paramètres
 * actuels, laisse l'utilisateur les ajuster (notamment basculer en plage
 * personnalisée), et ne déclenche la génération que sur clic Confirmer.
 *
 * # UX
 * - Le dialog s'ouvre avec les valeurs courantes pré-sélectionnées
 * - Bouton Confirmer focus par défaut → l'utilisateur peut juste presser
 *   Entrée pour valider sans modifier
 * - Section "Plage personnalisée" cachée par défaut, expandable au clic
 * - Annuler ferme sans rien faire
 */
@Component({
  selector: "vl-excel-export-confirm-dialog",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    HeaderDialogComponent,
    ChipsToggleComponent,
    BigButtonComponent,
    MatIconModule,
    MatSlideToggleModule,
    DatetimePickerComponent,
  ],
  templateUrl: "./excel-export-confirm-dialog.component.html",
  styleUrl: "./excel-export-confirm-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExcelExportConfirmDialogComponent {
  protected dialogRef = inject(
    MatDialogRef<ExcelExportConfirmDialogComponent, ExcelExportConfirmResult | null>,
  );
  protected data = inject<ExcelExportConfirmData>(MAT_DIALOG_DATA);

  /** Période sélectionnée (initialisée avec la valeur courante). */
  selectedPeriod = signal<any>(this.data.currentPeriod);

  /** Toggle "Plage personnalisée" — active les datepickers. */
  customRange = signal(false);

  /** Dates de la plage personnalisée (Date pour le picker, on convertit
   *  en ISO `yyyy-MM-dd` à la sortie). */
  customStart = signal<Date | null>(
    this.data.initialCustomStart ? new Date(this.data.initialCustomStart) : null,
  );
  customEnd = signal<Date | null>(
    this.data.initialCustomEnd ? new Date(this.data.initialCustomEnd) : null,
  );

  /** État live des toggles (clone pour ne pas muter le data d'entrée). */
  toggleStates = signal<Record<string, boolean>>(
    Object.fromEntries(
      (this.data.toggles ?? []).map((t) => [t.id, t.value]),
    ),
  );

  /** Validation : si custom range activé, les 2 dates doivent être saisies. */
  canConfirm = computed<boolean>(() => {
    if (!this.customRange()) return true;
    return !!this.customStart() && !!this.customEnd();
  });

  toggleValue(id: string, value: boolean) {
    this.toggleStates.update((cur) => ({ ...cur, [id]: value }));
  }

  onConfirm() {
    if (!this.canConfirm()) return;
    const result: ExcelExportConfirmResult = {
      toggles: this.toggleStates(),
    };
    if (this.customRange()) {
      const s = this.customStart();
      const e = this.customEnd();
      result.customStart = s ? this.toIso(s) : undefined;
      result.customEnd = e ? this.toIso(e) : undefined;
    } else {
      result.period = this.selectedPeriod();
    }
    this.dialogRef.close(result);
  }

  /** Convertit une `Date` en ISO `yyyy-MM-dd` (locale) sans heure. */
  private toIso(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  onCancel() {
    this.dialogRef.close(null);
  }
}
